import React, { Component } from 'react';
import { Container, Row, Col, Button, Card } from 'reactstrap';
import { Rating } from 'react-simple-star-rating';
import exitIntent from 'exit-intent';
import Swal from 'sweetalert2';

// --------------------------------------------------
// Extract domain + URL params (single source of truth)
// --------------------------------------------------
const url = new URL(window.location.href);

const variant = (url.searchParams.get('variant') || '').toLowerCase();
const id = url.searchParams.get('id');
const fid = url.searchParams.get('fid');

// Canonical funnel id: id wins over fid
const funnelId = id || fid;

// Footer domain extraction
const domain = window.location.href.replace('www.', '').split('//')[1].split('/')[0];

// --------------------------------------------------
// Variant-first API base resolver (variant → id → fid)
// --------------------------------------------------
function resolveApiBases({ variant, id, fid }) {
  // 1) Variant always wins if provided
  switch (variant) {
    case 'v2':
    case 'mpp':
      return {
        funnelsBase: 'https://admin-portal.cellphonecash.co/api',
        categoriesBase: 'https://admin-portal.cellphonecash.co/api',
      };

    case 'v1':
    case 'cpc':
    case 'cellphonecash':
    case 'legacy':
      return {
        funnelsBase: 'https://manage.cellphonecash.co/api',
        categoriesBase: 'https://manage.cellphonecash.co/api',
      };

    default:
      // fall through to id/fid inference
      break;
  }

  // 2) No (recognized) variant → infer from which id param is present
  if (id) {
    return {
      funnelsBase: 'https://manage.cellphonecash.co/api',
      categoriesBase: 'https://manage.cellphonecash.co/api',
    };
  }

  if (fid) {
    return {
      funnelsBase: 'https://manage.mobileprofitpro.com/api',
      categoriesBase: 'https://manage.mobileprofitpro.com/api',
    };
  }

  // 3) Nothing to go on
  throw new Error('Unable to resolve API base: missing variant, id, and fid');
}

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      headline1: '',
      headline2: '',
      header: '',
      products: [],
      bottomheadline: '',
      bottomtext: '',
      footertext: '',
      exitpopheadline: '',
      exitpopbody: '',
      cbid: '',
      popupFired: false,
    };
  }

  componentDidMount() {
    console.log('App loaded');
    console.log('Resolved params:', { variant, id, fid, funnelId });

    // Initialize exit-intent
    this.removeExitIntent = exitIntent({
      threshold: 1,
      maxDisplays: 2,
      eventThrottle: 100,
      onExitIntent: () => {
        if (!this.state.popupFired) {
          this.setState({ popupFired: true }, () => {
            // Fire SweetAlert2 popup after state update
            Swal.fire({
              title: `<strong>${this.state.exitpopheadline}</strong>`,
              html: this.state.exitpopbody,
              showCloseButton: false,
              showCancelButton: false,
              focusConfirm: false,
              confirmButtonText: 'Close This Popup And Choose The Best',
              confirmButtonColor: '#ff7518',
              confirmButtonAriaLabel: 'Thumbs up, great!',
            });
          });
        }
      },
    });

    let bases;
    try {
      bases = resolveApiBases({ variant, id, fid });
    } catch (e) {
      console.error(e);
      return;
    }

    const { funnelsBase, categoriesBase } = bases;

    // Fetch funnel and category data
    const fetchFunnel = (funnelUrl) => {
      fetch(funnelUrl)
        .then((res) => {
          if (!res.ok) throw new Error(`Network error ${res.status}`);
          return res.json();
        })
        .then((data) => {
          this.setState({ cbid: data.cbid });

          const categoryId = data.category?.id;
          if (!categoryId) throw new Error('Category ID missing');

          return fetch(`${categoriesBase}/categories/${categoryId}`);
        })
        .then((res) => {
          if (!res.ok) throw new Error(`Network error ${res.status}`);
          return res.json();
        })
        .then((data) => {
          if (data.headline2) {
            data.headline2 = data.headline2.replace('{Current Year}', new Date().getFullYear());
          }

          this.setState({
            headline1: data.headline1 || '',
            headline2: data.headline2 || '',
            header: data.header || '',
            products: data.products || [],
            bottomheadline: data.bottomheadline || '',
            bottomtext: data.bottomtext || '',
            footertext: data.footertext || '',
            exitpopheadline: data.exitpopheadline || '',
            exitpopbody: data.exitpopbody || '',
          });
        })
        .catch((err) => console.error('Fetch error:', err));
    };

    // Kick off funnel fetch (variant decides base, then id/fid decides funnel id)
    if (funnelId) {
      fetchFunnel(`${funnelsBase}/funnels/${funnelId}`);
    } else {
      console.warn('Missing funnel identifier param: expected ?id=... or ?fid=...');
    }
  }

  componentWillUnmount() {
    // Cleanup exit-intent listener if available
    if (typeof this.removeExitIntent === 'function') {
      this.removeExitIntent();
    }
  }

  // Render product bullets
  renderBullets(product) {
    const bullets = [];
    for (let i = 1; i <= 5; i++) {
      const key = `bullet${i}`;
      if (product[key]) {
        bullets.push(
          <li key={i}>
            <b>✓</b> {product[key]}
          </li>
        );
      }
    }
    return <ul style={{ listStyle: 'none', marginLeft: -28 }}>{bullets}</ul>;
  }

  // Card styling
  renderCardOpener(index) {
    return index === 0
      ? { boxShadow: '0px 0px 10px gray', borderColor: 'rgba(0,116,199,1)', marginBottom: 40 }
      : { boxShadow: '0px 0px 10px gray', marginBottom: 40 };
  }

  // Render product cards
  renderProducts(products) {
    if (!products || products.length === 0) return null;

    return products.map((product, index) => (
      <Card key={index} style={this.renderCardOpener(index)}>
        <Row style={{ marginBottom: 40 }}>
          <Col md="3" className="text-center">
            <img src={product.image?.url || ''} alt={product.product || ''} style={{ width: 146, marginTop: 40, marginBottom: 10 }} />
            <Rating initialValue={product.rating / 2} readonly allowFraction size={30} />
            <h2 style={{ color: 'rgba(0,116,199,1)', fontWeight: 'bold' }}>{product.rating}</h2>
            <p style={{ fontSize: 21.5 }}>Rating</p>
          </Col>
          <Col md="6">
            <h2 style={{ fontWeight: 'bold', marginTop: 30 }}>
              #{index + 1}: {product.product}
            </h2>
            <p style={{ fontSize: 21 }}>{product.bodytext}</p>
            {this.renderBullets(product)}
            <a
              href={product.hoplink + this.state.cbid}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 21, fontWeight: 'bold', textDecoration: 'none' }}
            >
              Get The Details On This Deal Now
            </a>
          </Col>
          <Col md="3">
            <Button
              style={{ marginTop: 80, marginLeft: 40 }}
              tag="a"
              color="warning"
              size="large"
              href={product.hoplink + this.state.cbid}
              target="_blank"
              rel="noopener noreferrer"
            >
              <h5 style={{ paddingTop: 5, fontWeight: 'bold', width: 140 }}>VIEW DEAL</h5>
            </Button>
          </Col>
        </Row>
      </Card>
    ));
  }

  render() {
    return (
      <div style={{ overflow: 'hidden' }}>
        <div className="rounded px-3 px-sm-4 py-3 py-sm-5">
          <Container style={{ maxWidth: 1150 }}>
            <Row>
              <Col>
                <Row style={{ marginBottom: 40 }}>
                  <Col style={{ marginLeft: 70, marginRight: 70 }}>
                    <h3>{this.state.headline1}</h3>
                    <h1 style={{ color: 'rgba(0,116,199,1)' }}>{this.state.headline2}</h1>
                    <p style={{ fontSize: 20 }} dangerouslySetInnerHTML={{ __html: this.state.header }} />
                  </Col>
                </Row>

                {this.renderProducts(this.state.products)}

                <Row style={{ marginBottom: 40 }}>
                  <Col style={{ marginLeft: 70, marginRight: 70 }}>
                    <h3 style={{ fontWeight: 'bold', marginBottom: 20 }}>{this.state.bottomheadline}</h3>
                    <p style={{ fontSize: 20 }} dangerouslySetInnerHTML={{ __html: this.state.bottomtext }} />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>

          <div
            style={{
              backgroundColor: 'rgba(192,192,192,1)',
              padding: 40,
              width: '120%',
              position: 'relative',
              right: '10%',
              top: 60,
            }}
          >
            <Container style={{ maxWidth: 1000 }} className="text-center">
              <p style={{ fontSize: 16 }} dangerouslySetInnerHTML={{ __html: this.state.footertext }} />
              <p style={{ fontSize: 16 }}>&copy;{domain}. ALL RIGHTS RESERVED.</p>
              <p style={{ fontSize: 12, marginTop: 40 }}>
                Please note that the owner/licensee of this website may receive a commission if you click a link on this website and make a purchase. This,
                however, has no bearing on the reviews and comparisons presented on this website. We will continue to do our best to assist you in finding the
                best solutions and products to assist you.
              </p>
            </Container>
          </div>
        </div>
      </div>
    );
  }
}

export default App;

// import React, { Component } from 'react';
// import { Container, Row, Col, Button, Card } from 'reactstrap';
// import { Rating } from 'react-simple-star-rating';
// import exitIntent from 'exit-intent';
// import Swal from 'sweetalert2';

// // Utility to get URL parameters
// function getParameterByName(name, url = window.location.href) {
//   name = name.replace(/[[\]]/g, '\\$&');
//   const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
//   const results = regex.exec(url);
//   if (!results) return null;
//   if (!results[2]) return '';
//   return decodeURIComponent(results[2].replace(/\+/g, ' '));
// }

// // Extract domain and query parameters
// const domain = window.location.href.replace('www.', '').split('//')[1].split('/')[0];
// const urlObj = new URL(window.location.href);
// const id = urlObj.searchParams.get('id');
// const fid = urlObj.searchParams.get('fid');

// class App extends Component {
//   constructor(props) {
//     super(props);
//     this.state = {
//       headline1: '',
//       headline2: '',
//       header: '',
//       products: [],
//       bottomheadline: '',
//       bottomtext: '',
//       footertext: '',
//       exitpopheadline: '',
//       exitpopbody: '',
//       cbid: '',
//       popupFired: false,
//     };
//   }

//   componentDidMount() {
//     console.log('App loaded');

//     // Initialize exit-intent
//     this.removeExitIntent = exitIntent({
//       threshold: 1,
//       maxDisplays: 2,
//       eventThrottle: 100,
//       onExitIntent: () => {
//         if (!this.state.popupFired) {
//           this.setState({ popupFired: true }, () => {
//             // Fire SweetAlert2 popup after state update
//             Swal.fire({
//               title: `<strong>${this.state.exitpopheadline}</strong>`,
//               html: this.state.exitpopbody,
//               showCloseButton: false,
//               showCancelButton: false,
//               focusConfirm: false,
//               confirmButtonText: 'Close This Popup And Choose The Best',
//               confirmButtonColor: '#ff7518',
//               confirmButtonAriaLabel: 'Thumbs up, great!',
//             });
//           });
//         }
//       },
//     });

//     // Fetch funnel and category data
//     const fetchFunnel = (url) => {
//       fetch(url)
//         .then((res) => {
//           if (!res.ok) throw new Error(`Network error ${res.status}`);
//           return res.json();
//         })
//         .then((data) => {
//           this.setState({ cbid: data.cbid });
//           const categoryId = data.category?.id;
//           if (!categoryId) throw new Error('Category ID missing');
//           if (fid) {
//             return fetch(`https://manage.mobileprofitpro.com/api/categories/${categoryId}`);
//           }
//           if (id) {
//             return fetch(`https://manage.cellphonecash.co/api/categories/${categoryId}`);
//           }
//           //return fetch(`https://manage.cellphonecash.co/api/categories/${categoryId}`);
//         })
//         .then((res) => {
//           if (!res.ok) throw new Error(`Network error ${res.status}`);
//           return res.json();
//         })
//         .then((data) => {
//           if (data.headline2) {
//             data.headline2 = data.headline2.replace('{Current Year}', new Date().getFullYear());
//           }
//           this.setState({
//             headline1: data.headline1 || '',
//             headline2: data.headline2 || '',
//             header: data.header || '',
//             products: data.products || [],
//             bottomheadline: data.bottomheadline || '',
//             bottomtext: data.bottomtext || '',
//             footertext: data.footertext || '',
//             exitpopheadline: data.exitpopheadline || '',
//             exitpopbody: data.exitpopbody || '',
//           });
//         })
//         .catch((err) => console.error('Fetch error:', err));
//     };

//     if (id) {
//       fetchFunnel(`https://manage.cellphonecash.co/api/funnels/${getParameterByName('id')}`);
//     } else if (fid) {
//       fetchFunnel(`https://manage.mobileprofitpro.com/api/funnels/${getParameterByName('fid')}`);
//     }
//   }

//   // Render product bullets
//   renderBullets(product) {
//     const bullets = [];
//     for (let i = 1; i <= 5; i++) {
//       const key = `bullet${i}`;
//       if (product[key]) {
//         bullets.push(
//           <li key={i}>
//             <b>✓</b> {product[key]}
//           </li>
//         );
//       }
//     }
//     return <ul style={{ listStyle: 'none', marginLeft: -28 }}>{bullets}</ul>;
//   }

//   // Card styling
//   renderCardOpener(index) {
//     return index === 0
//       ? { boxShadow: '0px 0px 10px gray', borderColor: 'rgba(0,116,199,1)', marginBottom: 40 }
//       : { boxShadow: '0px 0px 10px gray', marginBottom: 40 };
//   }

//   // Render product cards
//   renderProducts(products) {
//     if (!products || products.length === 0) return null;

//     return products.map((product, index) => (
//       <Card key={index} style={this.renderCardOpener(index)}>
//         <Row style={{ marginBottom: 40 }}>
//           <Col md="3" className="text-center">
//             <img src={product.image?.url || ''} alt={product.product || ''} style={{ width: 146, marginTop: 40, marginBottom: 10 }} />
//             <Rating initialValue={product.rating / 2} readonly allowFraction size={30} />
//             <h2 style={{ color: 'rgba(0,116,199,1)', fontWeight: 'bold' }}>{product.rating}</h2>
//             <p style={{ fontSize: 21.5 }}>Rating</p>
//           </Col>
//           <Col md="6">
//             <h2 style={{ fontWeight: 'bold', marginTop: 30 }}>
//               #{index + 1}: {product.product}
//             </h2>
//             <p style={{ fontSize: 21 }}>{product.bodytext}</p>
//             {this.renderBullets(product)}
//             <a
//               href={product.hoplink + this.state.cbid}
//               target="_blank"
//               rel="noopener noreferrer"
//               style={{ fontSize: 21, fontWeight: 'bold', textDecoration: 'none' }}
//             >
//               Get The Details On This Deal Now
//             </a>
//           </Col>
//           <Col md="3">
//             <Button
//               style={{ marginTop: 80, marginLeft: 40 }}
//               tag="a"
//               color="warning"
//               size="large"
//               href={product.hoplink + this.state.cbid}
//               target="_blank"
//               rel="noopener noreferrer"
//             >
//               <h5 style={{ paddingTop: 5, fontWeight: 'bold', width: 140 }}>VIEW DEAL</h5>
//             </Button>
//           </Col>
//         </Row>
//       </Card>
//     ));
//   }

//   render() {
//     return (
//       <div style={{ overflow: 'hidden' }}>
//         <div className="rounded px-3 px-sm-4 py-3 py-sm-5">
//           <Container style={{ maxWidth: 1150 }}>
//             <Row>
//               <Col>
//                 <Row style={{ marginBottom: 40 }}>
//                   <Col style={{ marginLeft: 70, marginRight: 70 }}>
//                     <h3>{this.state.headline1}</h3>
//                     <h1 style={{ color: 'rgba(0,116,199,1)' }}>{this.state.headline2}</h1>
//                     <p style={{ fontSize: 20 }} dangerouslySetInnerHTML={{ __html: this.state.header }} />
//                   </Col>
//                 </Row>
//                 {this.renderProducts(this.state.products)}
//                 <Row style={{ marginBottom: 40 }}>
//                   <Col style={{ marginLeft: 70, marginRight: 70 }}>
//                     <h3 style={{ fontWeight: 'bold', marginBottom: 20 }}>{this.state.bottomheadline}</h3>
//                     <p style={{ fontSize: 20 }} dangerouslySetInnerHTML={{ __html: this.state.bottomtext }} />
//                   </Col>
//                 </Row>
//               </Col>
//             </Row>
//           </Container>
//           <div
//             style={{
//               backgroundColor: 'rgba(192,192,192,1)',
//               padding: 40,
//               width: '120%',
//               position: 'relative',
//               right: '10%',
//               top: 60,
//             }}
//           >
//             <Container style={{ maxWidth: 1000 }} className="text-center">
//               <p style={{ fontSize: 16 }} dangerouslySetInnerHTML={{ __html: this.state.footertext }} />
//               <p style={{ fontSize: 16 }}>&copy;{domain}. ALL RIGHTS RESERVED.</p>
//               <p style={{ fontSize: 12, marginTop: 40 }}>
//                 Please note that the owner/licensee of this website may receive a commission if you click a link on this website and make a purchase. This,
//                 however, has no bearing on the reviews and comparisons presented on this website. We will continue to do our best to assist you in finding the
//                 best solutions and products to assist you.
//               </p>
//             </Container>
//           </div>
//         </div>
//       </div>
//     );
//   }
// }

// export default App;
